/**
 * Where a swipe has to begin for it to mean "go forward".
 *
 * WHY AN EDGE AND NOT THE WHOLE SCREEN. A gesture that starts anywhere fights
 * everything that scrolls sideways: the sport carousel, the segmented tabs,
 * the cards. The edge is what both platforms already use for exactly this, so
 * it costs nothing to learn and collides with nothing.
 *
 * Twenty-five pixels, the width Ionic's own back gesture uses. Matching it
 * means the two gestures feel like one family rather than two ideas.
 */
export const EDGE_WIDTH_PX = 25;

/**
 * How far the finger has to travel before the page moves.
 *
 * Short enough to feel immediate, long enough that the edge of a thumb resting
 * on the side of the phone never triggers it.
 */
export const FORWARD_THRESHOLD_PX = 60;

export interface SwipeAttempt {
  /** Where the finger landed, in pixels from the left of the screen. */
  startX: number;
  /** How far it travelled. Negative is leftwards. */
  deltaX: number;
  /** How far it wandered up or down on the way. */
  deltaY: number;
  screenWidth: number;
}

/**
 * Whether this movement asks to go forward.
 *
 * Starts against the right edge, travels left, and travels further sideways
 * than up or down. That last test is what separates a deliberate swipe from
 * the diagonal drift of a thumb scrolling a list.
 */
export function isForwardSwipe(attempt: SwipeAttempt): boolean {
  const startedAtRightEdge = attempt.startX >= attempt.screenWidth - EDGE_WIDTH_PX;
  const travelledLeftEnough = attempt.deltaX <= -FORWARD_THRESHOLD_PX;
  const staysHorizontal = Math.abs(attempt.deltaX) > Math.abs(attempt.deltaY);

  return startedAtRightEdge && travelledLeftEnough && staysHorizontal;
}

/**
 * Whether this movement asks to go back: the mirror of the forward rule.
 *
 * Starts against the left edge, travels right, and stays more sideways than
 * up or down. Ionic's own back gesture keeps the left edge wherever it can go
 * back (it sits above this one); this rule only decides the screens it cannot
 * reach, see `setupBackSwipe` in app.component.
 */
export function isBackSwipe(attempt: SwipeAttempt): boolean {
  const startedAtLeftEdge = attempt.startX <= EDGE_WIDTH_PX;
  const travelledRightEnough = attempt.deltaX >= FORWARD_THRESHOLD_PX;
  const staysHorizontal = Math.abs(attempt.deltaX) > Math.abs(attempt.deltaY);

  return startedAtLeftEdge && travelledRightEnough && staysHorizontal;
}

/**
 * The tab roots. The bottom bar moves between them, so a swipe from the left
 * edge does nothing there, as in every iPhone application with tabs.
 */
export const TAB_ROOTS = ['/home/explore', '/home/activity', '/home/message', '/home/profile'] as const;

/** Whether an address, query and fragment aside, is one of the tab roots. */
export const isATabRoot = (url: string): boolean =>
  (TAB_ROOTS as readonly string[]).includes(url.split(/[?#]/)[0].replace(/\/+$/, ''));
